import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, SERVER_ORIGIN } from '../services/api';
import { byRecentAssignment } from '../utils/people';
import {
  Plus, X, Edit2, Trash2, Users, DollarSign,
  Calendar, CheckCircle, FileText, ArrowRight, Receipt, Camera,
} from 'lucide-react';
import { toast } from 'react-toastify';

const STATUSES = ['draft', 'scheduled', 'in_progress', 'complete', 'invoiced', 'paid', 'cancelled'] as const;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  complete: 'Complete',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-brand-100 text-brand-700',
  in_progress: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  invoiced: 'bg-brand-100 text-brand-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
};

const UPLOADS_BASE = SERVER_ORIGIN;

const SERVICE_TYPES: Record<string, string> = {
  mulch: 'Mulch', cleanup: 'Cleanup', trimming: 'Bush / Hedge Trimming',
  landscaping: 'Landscaping', irrigation: 'Irrigation', fence: 'Fence',
  sod: 'Sod', drainage: 'Drainage', planting: 'Planting',
  tree_trimming: 'Tree Trimming', rocks: 'Rocks / Gravel',
  project: 'General Project', other: 'Other',
};
function fmtServiceType(st?: string | null) {
  if (!st) return '';
  return SERVICE_TYPES[st.toLowerCase()] || st.split(/[_\s]+/).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

const emptyForm = {
  customer_id: '', property_id: '', title: '', description: '', service_type: '',
  date_start: '', date_end: '', time_start: '', status: 'draft',
  required_headcount: '1',
  is_open_shift: false, notes: '', crew_notes: '', price: '',
};

function fmtDate(d?: string) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtMoney(n?: number | null) {
  if (n == null) return '';
  return `$${Number(n).toFixed(2)}`;
}

export default function Projects() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState<'new' | 'edit' | 'detail' | null>(null);
  const [memoOpen, setMemoOpen] = useState(false);
  // For new-project flow: 'choose' → 'pick-quote' → 'form'  or  'choose' → 'form'
  const [newStep, setNewStep] = useState<'choose' | 'pick-quote' | 'form'>('choose');
  const [quoteSearch, setQuoteSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [showComplete, setShowComplete] = useState(false);
  const [completeInvoice, setCompleteInvoice] = useState<any | null>(null);

  const { data: projectsData, isLoading } = useQuery(['projects', filterStatus], () =>
    api.get('/projects', { params: filterStatus ? { status: filterStatus } : {} }).then(r => r.data as { projects: any[]; job_projects?: any[] })
  );

  const { data: custData } = useQuery('customers-list', () =>
    api.get('/customers').then(r => r.data.customers as any[])
  );

  const { data: memberData } = useQuery('employees-all', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );

  const { data: quotesData } = useQuery('quotes-all', () =>
    api.get('/quotes').then(r => r.data.quotes as any[])
  );

  const { data: detailData, refetch: refetchDetail } = useQuery(
    ['project-detail', selectedProject?.id],
    () => api.get(`/projects/${selectedProject!.id}`).then(r => r.data),
    { enabled: modal === 'detail' && !!selectedProject }
  );

  const { data: propData } = useQuery(
    ['props-for-project', form.customer_id],
    () => api.get('/properties', { params: { customer_id: form.customer_id } }).then(r => r.data.properties as any[]),
    { enabled: !!form.customer_id }
  );

  const saveProject = useMutation(async (statusOverride?: string) => {
    const payload = {
      ...form,
      status: statusOverride || form.status,
      required_headcount: Number(form.required_headcount) || 1,
      price: form.price ? Number(form.price) : null,
      customer_id: Number(form.customer_id),
      property_id: form.property_id ? Number(form.property_id) : null,
    };
    if (modal === 'edit' && selectedProject) {
      await api.put(`/projects/${selectedProject.id}`, payload);
    } else {
      await api.post('/projects', payload);
    }
  }, {
    onSuccess: () => {
      qc.invalidateQueries('projects');
      toast.success(modal === 'edit' ? 'Project updated' : 'Project created');
      setModal(null); setForm(emptyForm); setSelectedProject(null);
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to save'); },
  });

  const deleteProject = useMutation(async (id: number) => { await api.delete(`/projects/${id}`); }, {
    onSuccess: () => { qc.invalidateQueries('projects'); toast.success('Project deleted'); setModal(null); setSelectedProject(null); },
    onError: () => { toast.error('Failed to delete project'); },
  });

  const photoInputRef = useRef<HTMLInputElement>(null);

  const uploadPhotos = useMutation(async (files: FileList) => {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('photos', f));
    await api.post(`/projects/${selectedProject!.id}/photos`, fd);
  }, {
    onSuccess: () => { refetchDetail(); toast.success('Photo added'); },
    onError: () => { toast.error('Failed to upload photo'); },
  });

  const deletePhotoMut = useMutation(async (photoId: number) => {
    await api.delete(`/projects/${selectedProject!.id}/photos/${photoId}`);
  }, {
    onSuccess: () => { refetchDetail(); toast.success('Photo removed'); },
    onError: () => { toast.error('Failed to remove photo'); },
  });

  const removeAssignment = useMutation(async ({ projectId, cmId }: { projectId: number; cmId: number }) => {
    await api.delete(`/projects/${projectId}/assign/${cmId}`);
  }, {
    onSuccess: () => { refetchDetail(); toast.success('Removed'); },
    onError: () => { toast.error('Failed to remove'); },
  });

  const [addingMember, setAddingMember] = useState('');
  const addAssignment = useMutation(async () => {
    await api.post(`/projects/${selectedProject!.id}/assign`, { crew_member_id: Number(addingMember) });
  }, {
    onSuccess: () => { refetchDetail(); setAddingMember(''); toast.success('Crew member added'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to add'); },
  });

  const [newLiForm, setNewLiForm] = useState({ service_name: '', description: '', quantity: '1', unit_price: '', internal_cost: '' });
  const [addingLi, setAddingLi] = useState(false);
  const [editingLi, setEditingLi] = useState<any | null>(null);

  const addLineItem = useMutation(async () => {
    await api.post(`/projects/${selectedProject!.id}/line-items`, {
      service_name: newLiForm.service_name,
      description: newLiForm.description,
      quantity: Number(newLiForm.quantity) || 1,
      unit_price: Number(newLiForm.unit_price),
      internal_cost: newLiForm.internal_cost ? Number(newLiForm.internal_cost) : null,
    });
  }, {
    onSuccess: () => { refetchDetail(); qc.invalidateQueries('projects'); setAddingLi(false); setNewLiForm({ service_name: '', description: '', quantity: '1', unit_price: '', internal_cost: '' }); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to add'); },
  });

  const updateLineItem = useMutation(async (li: any) => {
    await api.put(`/projects/${selectedProject!.id}/line-items/${li.id}`, {
      service_name: li.service_name,
      description: li.description,
      quantity: Number(li.quantity),
      unit_price: Number(li.unit_price),
      internal_cost: li.internal_cost != null ? Number(li.internal_cost) : null,
    });
  }, {
    onSuccess: () => { refetchDetail(); qc.invalidateQueries('projects'); setEditingLi(null); },
    onError: () => { toast.error('Failed to update'); },
  });

  const deleteLineItem = useMutation(async (liId: number) => {
    await api.delete(`/projects/${selectedProject!.id}/line-items/${liId}`);
  }, {
    onSuccess: () => { refetchDetail(); qc.invalidateQueries('projects'); },
    onError: () => { toast.error('Failed to delete'); },
  });

  const createInvoice = useMutation(async () => {
    const p = detailData?.project || selectedProject;
    const r = await api.post('/invoices', {
      customer_id: p.customer_id,
      project_id: p.id,
      invoice_type: 'project',
    });
    return r.data;
  }, {
    onSuccess: (data: any) => {
      refetchDetail();
      qc.invalidateQueries('projects');
      toast.success(data.message || 'Invoice created');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to create invoice'); },
  });

  const completeProject = useMutation(async ({ projectId, customerId, createInv }: { projectId: number; customerId: number; createInv: boolean }) => {
    await api.post(`/projects/${projectId}/complete`);
    if (createInv) {
      const r = await api.post('/invoices', { customer_id: customerId, project_id: projectId, invoice_type: 'project' });
      return r.data;
    }
    return null;
  }, {
    onSuccess: (data: any) => {
      refetchDetail();
      qc.invalidateQueries('projects');
      qc.invalidateQueries('billing-unbilled');
      qc.invalidateQueries('billing-dashboard');
      setShowComplete(false);
      if (data?.invoice) {
        setCompleteInvoice(data.invoice);
        toast.success(`Project complete · Invoice ${data.invoice.invoice_number} created`);
      } else {
        toast.success('Project marked complete');
      }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed'); },
  });

  function openNew() {
    setForm(emptyForm);
    setSelectedProject(null);
    setNewStep('choose');
    setQuoteSearch('');
    setModal('new');
  }

  function prefillFromQuote(q: any) {
    setForm({
      ...emptyForm,
      customer_id: String(q.customer_id),
      property_id: q.property_id ? String(q.property_id) : '',
      title: q.title || '',
      description: q.notes || '',
      price: q.total ? String(q.total) : '',
      notes: '',
    });
    setNewStep('form');
    setQuoteSearch('');
  }
  function openEdit(p: any) {
    setSelectedProject(p);
    setForm({
      customer_id: String(p.customer_id), property_id: String(p.property_id || ''),
      title: p.title, description: p.description || '', service_type: p.service_type || '',
      date_start: p.date_start || '', date_end: p.date_end || '',
      time_start: p.time_start || '', status: p.status,
      required_headcount: String(p.required_headcount || 1),
      is_open_shift: !!p.is_open_shift, notes: p.notes || '', crew_notes: p.crew_notes || '',
      price: p.price ?? '',
    });
    setModal('edit');
  }
  function openDetail(p: any) {
    setSelectedProject(p);
    setModal('detail');
    setShowComplete(false);
    setCompleteInvoice(null);
  }

  const pf = (k: string) => (e: React.ChangeEvent<any>) => setForm((f: any) => ({ ...f, [k]: e.target.value }));
  const projects: any[] = projectsData?.projects || [];
  // /projects?open=4 — the link on a converted bid ("Converted to Project #4") lands on that project, open.
  const location = useLocation();
  const openedRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const want = Number(new URLSearchParams(location.search).get('open')) || null;
    if (!want || openedRef.current === want) return;
    const p = projects.find((x: any) => Number(x.id) === want);
    if (p) { openedRef.current = want; openDetail(p); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, projectsData]);
  const jobProjects: any[] = projectsData?.job_projects || [];
  const customers = custData || [];
  const members = memberData || [];
  const properties = propData || [];
  const assignments = detailData?.assignments || [];
  const lineItems = detailData?.line_items || [];
  const photos = detailData?.photos || [];
  const assignedIds = new Set(assignments.map((a: any) => a.crew_member_id));
  const allQuotes = quotesData || [];
  const filteredQuotes = quoteSearch.trim()
    ? allQuotes.filter((q: any) =>
        q.title?.toLowerCase().includes(quoteSearch.toLowerCase()) ||
        q.customer_name?.toLowerCase().includes(quoteSearch.toLowerCase()) ||
        q.address?.toLowerCase().includes(quoteSearch.toLowerCase())
      )
    : allQuotes;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center gap-2">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Project Work</h1>
        <div className="flex gap-2">
          <button onClick={() => setMemoOpen(true)} className="btn-primary">
            🎙 Voice memo
          </button>
          <button onClick={openNew} className="press px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 inline-flex items-center">
            <Plus className="h-4 w-4 mr-1" /> Full form
          </button>
        </div>
      </div>
      {memoOpen && <MemoModal customers={customers} onClose={() => setMemoOpen(false)}
        onDone={() => { setMemoOpen(false); qc.invalidateQueries('projects'); }} />}

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterStatus('')}
          className={`press px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${!filterStatus ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          All
        </button>
        {STATUSES.map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`press px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${filterStatus === s ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {STATUS_LABELS[s] || s}
          </button>
        ))}
      </div>

      {/* List */}
      {/* Owner rule: any job that isn't a mow is a project. These live on the
          jobs side (fence staining, mulch, cleanups) and open on the job page. */}
      {!filterStatus && jobProjects.length > 0 && (
        <div className="card p-4 mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">One-off jobs ({jobProjects.length})</p>
          <div className="divide-y divide-gray-100">
            {jobProjects.map((j: any) => (
              <button key={`job-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
                className="w-full text-left py-2 flex items-center justify-between gap-3 hover:bg-gray-50 rounded">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{j.title || fmtServiceType(j.service_type)} <span className="text-xs text-gray-500 font-normal">· {j.customer_name}</span></p>
                  <p className="text-xs text-gray-500 truncate">{fmtServiceType(j.service_type)}{j.date_start ? ` · ${fmtDate(j.date_start)}` : ''}{j.address ? ` · ${String(j.address).split(',')[0]}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${j.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{j.status}</span>
                  {j.price ? <span className="text-xs text-green-700 font-semibold">${Number(j.price).toFixed(2)}</span> : null}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">No projects yet. Create your first one!</div>
      ) : (
        <div className="space-y-3">
          {projects.map((p: any) => (
            <div key={p.id} className="card px-4 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(p)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{p.title}</span>
                    <span className="text-xs text-gray-500">#{p.id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                    {p.service_type && (
                      <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full font-medium">
                        {fmtServiceType(p.service_type)}
                      </span>
                    )}
                    {p.is_open_shift ? <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">open shift</span> : null}
                  </div>
                  <div className="flex gap-3 mt-1 flex-wrap">
                    <span className="text-sm text-gray-600">{p.customer_name}</span>
                    {p.address && <span className="text-xs text-gray-500">{p.address.split(',')[0]}</span>}
                  </div>
                  <div className="flex gap-3 mt-1 flex-wrap">
                    {p.date_start && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />{fmtDate(p.date_start)}{p.date_end && p.date_end !== p.date_start ? ` – ${fmtDate(p.date_end)}` : ''}
                      </span>
                    )}
                    {p.price && <span className="flex items-center gap-1 text-xs text-green-700 font-semibold"><DollarSign className="h-3 w-3" />{Number(p.price).toFixed(2)}</span>}
                    {p.required_headcount > 1 && <span className="flex items-center gap-1 text-xs text-gray-500"><Users className="h-3 w-3" />{p.required_headcount} crew</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); openEdit(p); }}
                    className="p-1.5 text-gray-500 hover:text-brand-600 rounded" title="Edit">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={e => {
                    e.stopPropagation();
                    if (window.confirm(`Delete "${p.title}"?`)) deleteProject.mutate(p.id);
                  }} className="p-1.5 text-gray-500 hover:text-red-500 rounded" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New project — source choice */}
      {modal === 'new' && newStep === 'choose' && (
        <Modal title="New Project Work" onClose={() => { setModal(null); setForm(emptyForm); }}>
          <p className="text-sm text-gray-500 mb-5">How would you like to start?</p>
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => { setNewStep('pick-quote'); setQuoteSearch(''); }}
              className="flex items-center gap-4 p-4 border-2 border-brand-200 rounded-xl hover:border-brand-500 hover:bg-brand-50 text-left transition-colors group"
            >
              <div className="h-10 w-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-200">
                <FileText className="h-5 w-5 text-brand-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Create from Quote</p>
                <p className="text-xs text-gray-500 mt-0.5">Prefill from an existing accepted or sent quote</p>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-brand-600 flex-shrink-0" />
            </button>
            <button
              onClick={() => setNewStep('form')}
              className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 text-left transition-colors group"
            >
              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200">
                <Plus className="h-5 w-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Custom Project</p>
                <p className="text-xs text-gray-500 mt-0.5">Start with a blank form</p>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-gray-600 flex-shrink-0" />
            </button>
          </div>
        </Modal>
      )}

      {/* New project — quote picker */}
      {modal === 'new' && newStep === 'pick-quote' && (
        <Modal title="Select a Quote" onClose={() => { setModal(null); setForm(emptyForm); }}>
          <div className="mb-3">
            <input
              className="input w-full"
              placeholder="Search by title, customer, or address…"
              value={quoteSearch}
              onChange={e => setQuoteSearch(e.target.value)}
              autoFocus
            />
          </div>
          {filteredQuotes.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No quotes found.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {filteredQuotes.map((q: any) => (
                <button
                  key={q.id}
                  onClick={() => prefillFromQuote(q)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-brand-400 hover:bg-brand-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{q.title}</p>
                      <p className="text-xs text-gray-500">{q.customer_name}</p>
                      {q.address && <p className="text-xs text-gray-500 truncate">{q.address}</p>}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {q.total != null && (
                        <p className="text-sm font-semibold text-green-700">${Number(q.total).toFixed(2)}</p>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        q.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        q.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{q.status}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="pt-3 border-t border-gray-100 mt-3 flex justify-between">
            <button onClick={() => setNewStep('choose')} className="btn-secondary text-sm">← Back</button>
            <button onClick={() => setModal(null)} className="btn-secondary text-sm">Cancel</button>
          </div>
        </Modal>
      )}

      {/* New/Edit modal — project form */}
      {((modal === 'new' && newStep === 'form') || modal === 'edit') && (
        <Modal title={modal === 'edit' ? 'Edit Project Work' : 'New Project Work'} onClose={() => { setModal(null); setForm(emptyForm); }}>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <Field label="Customer *">
              <select value={form.customer_id} onChange={pf('customer_id')} className="input">
                <option value="">— Select customer —</option>
                {customers.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Property">
              <select value={form.property_id} onChange={pf('property_id')} className="input" disabled={!form.customer_id}>
                <option value="">— Select property —</option>
                {properties.map((p: any) => {
                  const addr = [p.address, p.city, p.state].filter(Boolean).join(', ');
                  return <option key={p.id} value={p.id}>{p.label ? `${p.label} — ${addr}` : addr}</option>;
                })}
              </select>
            </Field>
            <Field label="Title *">
              <input value={form.title} onChange={pf('title')} className="input" placeholder="e.g. Mulch Install, Spring Cleanup" />
            </Field>
            <Field label="Service Type">
              <select value={form.service_type} onChange={pf('service_type')} className="input">
                <option value="">— Select type —</option>
                <option value="mulch">Mulch</option>
                <option value="cleanup">Cleanup</option>
                <option value="trimming">Bush / Hedge Trimming</option>
                <option value="tree_trimming">Tree Trimming</option>
                <option value="landscaping">Landscaping</option>
                <option value="planting">Planting</option>
                <option value="irrigation">Irrigation</option>
                <option value="fence">Fence</option>
                <option value="sod">Sod</option>
                <option value="drainage">Drainage</option>
                <option value="rocks">Rocks / Gravel</option>
                <option value="project">General Project</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Description">
              <textarea value={form.description} onChange={pf('description')} className="input" rows={2} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start Date">
                <input type="date" value={form.date_start} onChange={pf('date_start')} className="input" />
              </Field>
              <Field label="End Date">
                <input type="date" value={form.date_end} onChange={pf('date_end')} className="input" />
              </Field>
              <Field label="Start Time">
                <input type="time" value={form.time_start} onChange={pf('time_start')} className="input" />
              </Field>
              {modal === 'edit' && (
                <Field label="Status">
                  <select value={form.status} onChange={pf('status')} className="input">
                    {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Required Crew">
                <input type="number" value={form.required_headcount} onChange={pf('required_headcount')} className="input" min="1" />
              </Field>
              <Field label="Price ($)">
                <input type="number" value={form.price} onChange={pf('price')} className="input" step="0.01" />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.is_open_shift}
                onChange={e => setForm((f: any) => ({ ...f, is_open_shift: e.target.checked }))}
                className="rounded text-brand-600 focus:ring-brand-500" />
              Open shift — crew can claim this project
            </label>
            <Field label="Internal Notes">
              <textarea value={form.notes} onChange={pf('notes')} className="input" rows={2} />
            </Field>
            <Field label="Crew Notes (shown to assigned crew)">
              <textarea value={form.crew_notes} onChange={pf('crew_notes')} className="input" rows={2} />
            </Field>
          </div>
          <div className="pt-4 border-t border-gray-100 mt-4">
            <div className="flex justify-between gap-3">
              {modal === 'new' && (
                <button onClick={() => setNewStep('choose')} className="btn-secondary text-sm">← Back</button>
              )}
              <div className="flex gap-3 ml-auto">
                <button onClick={() => { setModal(null); setForm(emptyForm); }} className="btn-secondary">Cancel</button>
                {modal === 'new' ? (
                  <>
                    <button onClick={() => saveProject.mutate('draft')}
                      disabled={saveProject.isLoading || !form.customer_id || !form.title}
                      className="btn-secondary">
                      {saveProject.isLoading ? 'Saving…' : 'Save Draft'}
                    </button>
                    <button onClick={() => saveProject.mutate('scheduled')}
                      disabled={saveProject.isLoading || !form.customer_id || !form.title}
                      className="btn-primary">
                      {saveProject.isLoading ? 'Saving…' : 'Create Project Work'}
                    </button>
                  </>
                ) : (
                  <button onClick={() => saveProject.mutate(undefined)}
                    disabled={saveProject.isLoading || !form.customer_id || !form.title}
                    className="btn-primary">
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Detail modal */}
      {modal === 'detail' && selectedProject && (
        <Modal title={selectedProject.title} onClose={() => { setModal(null); setSelectedProject(null); setShowComplete(false); setCompleteInvoice(null); }}>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[selectedProject.status] || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_LABELS[selectedProject.status] || selectedProject.status}
              </span>
              {selectedProject.is_open_shift && <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">open shift</span>}
            </div>
            <div className="text-sm space-y-1 text-gray-700">
              {selectedProject.service_type && (
                <p><span className="font-medium">Service:</span> {fmtServiceType(selectedProject.service_type)}</p>
              )}
              <p><span className="font-medium">Customer:</span> {selectedProject.customer_name}</p>
              {selectedProject.address && <p><span className="font-medium">Property:</span> {selectedProject.address}</p>}
              {selectedProject.date_start && <p><span className="font-medium">Date:</span> {fmtDate(selectedProject.date_start)}{selectedProject.date_end && selectedProject.date_end !== selectedProject.date_start ? ` – ${fmtDate(selectedProject.date_end)}` : ''}</p>}
              {selectedProject.time_start && <p><span className="font-medium">Start time:</span> {selectedProject.time_start}</p>}
              {selectedProject.price && <p><span className="font-medium">Price:</span> {fmtMoney(selectedProject.price)}</p>}
              {selectedProject.description && <p className="text-gray-500">{selectedProject.description}</p>}
              {selectedProject.crew_notes && <p className="text-orange-600 italic">Crew notes: {selectedProject.crew_notes}</p>}
            </div>

            {/* Line items */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Line Items</p>
                {!addingLi && (
                  <button onClick={() => setAddingLi(true)}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Add
                  </button>
                )}
              </div>
              <div className="space-y-1 mb-2">
                {lineItems.length === 0 && !addingLi && (
                  <p className="text-sm text-gray-500 italic">No line items. Add items to build the project total.</p>
                )}
                {lineItems.map((li: any) => (
                  editingLi?.id === li.id ? (
                    <div key={li.id} className="bg-brand-50 rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input value={editingLi.service_name}
                          onChange={e => setEditingLi((x: any) => ({ ...x, service_name: e.target.value }))}
                          className="input text-sm col-span-2" placeholder="Service name *" />
                        <input value={editingLi.description}
                          onChange={e => setEditingLi((x: any) => ({ ...x, description: e.target.value }))}
                          className="input text-sm col-span-2" placeholder="Description (optional)" />
                        <input type="number" value={editingLi.quantity}
                          onChange={e => setEditingLi((x: any) => ({ ...x, quantity: e.target.value }))}
                          className="input text-sm" placeholder="Qty" />
                        <input type="number" value={editingLi.unit_price}
                          onChange={e => setEditingLi((x: any) => ({ ...x, unit_price: e.target.value }))}
                          className="input text-sm" placeholder="Unit price" step="0.01" />
                        <input type="number" value={editingLi.internal_cost ?? ''}
                          onChange={e => setEditingLi((x: any) => ({ ...x, internal_cost: e.target.value }))}
                          className="input text-sm" placeholder="Internal cost (private)" step="0.01" />
                        <div className="self-center text-sm font-semibold text-gray-700">
                          = {fmtMoney(Number(editingLi.quantity || 1) * Number(editingLi.unit_price || 0))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => updateLineItem.mutate(editingLi)} disabled={updateLineItem.isLoading}
                          className="btn-primary text-xs py-1 px-3">Save</button>
                        <button onClick={() => setEditingLi(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div key={li.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 group">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{li.service_name}</p>
                        {li.description && <p className="text-xs text-gray-500">{li.description}</p>}
                        {li.quantity !== 1 && <p className="text-xs text-gray-500">{li.quantity} × {fmtMoney(li.unit_price)}</p>}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-sm font-semibold text-gray-900">{fmtMoney(li.total)}</span>
                        <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditingLi({ ...li })}
                            className="p-1 text-gray-500 hover:text-brand-600 rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                          <button onClick={() => { if (window.confirm('Remove this item?')) deleteLineItem.mutate(li.id); }}
                            className="p-1 text-gray-500 hover:text-red-500 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  )
                ))}
                {addingLi && (
                  <div className="bg-brand-50 rounded-lg p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input value={newLiForm.service_name}
                        onChange={e => setNewLiForm(f => ({ ...f, service_name: e.target.value }))}
                        className="input text-sm col-span-2" placeholder="Service name *" autoFocus />
                      <input value={newLiForm.description}
                        onChange={e => setNewLiForm(f => ({ ...f, description: e.target.value }))}
                        className="input text-sm col-span-2" placeholder="Description (optional)" />
                      <input type="number" value={newLiForm.quantity}
                        onChange={e => setNewLiForm(f => ({ ...f, quantity: e.target.value }))}
                        className="input text-sm" placeholder="Qty" />
                      <input type="number" value={newLiForm.unit_price}
                        onChange={e => setNewLiForm(f => ({ ...f, unit_price: e.target.value }))}
                        className="input text-sm" placeholder="Unit price *" step="0.01" />
                      <input type="number" value={newLiForm.internal_cost}
                        onChange={e => setNewLiForm(f => ({ ...f, internal_cost: e.target.value }))}
                        className="input text-sm" placeholder="Internal cost (private)" step="0.01" />
                      <div className="self-center text-sm font-semibold text-gray-700">
                        = {fmtMoney(Number(newLiForm.quantity || 1) * Number(newLiForm.unit_price || 0))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => addLineItem.mutate()} disabled={addLineItem.isLoading || !newLiForm.service_name || !newLiForm.unit_price}
                        className="btn-primary text-xs py-1 px-3">Add</button>
                      <button onClick={() => setAddingLi(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              {lineItems.length > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">Project Total</span>
                  <span className="text-base font-bold text-green-700">
                    {fmtMoney(lineItems.reduce((s: number, li: any) => s + (li.total || 0), 0))}
                  </span>
                </div>
              )}
            </div>

            {/* Invoice */}
            {completeInvoice && (
              <div className="border border-brand-200 rounded-lg bg-brand-50 p-3 flex items-center justify-between mb-1">
                <div>
                  <p className="text-sm font-medium text-brand-900">Invoice {completeInvoice.invoice_number} created</p>
                  <p className="text-xs text-gray-500">Draft · Ready to send from Billing</p>
                </div>
                <button onClick={() => setCompleteInvoice(null)} className="text-xs text-gray-500 hover:text-gray-600 ml-3">✕</button>
              </div>
            )}
            {(selectedProject.status === 'complete' || (detailData?.project?.invoice_id)) && (
              <div className="border-t border-gray-100 pt-3">
                {(detailData?.project?.invoice_id || selectedProject.invoice_id) ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Receipt className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-600">Invoiced:</span>
                    <span className="font-medium text-gray-900">
                      {detailData?.project?.invoice_number || selectedProject.invoice_number}
                    </span>
                    {(detailData?.project?.invoice_status || selectedProject.invoice_status) && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        (detailData?.project?.invoice_status || selectedProject.invoice_status) === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : (detailData?.project?.invoice_status || selectedProject.invoice_status) === 'sent'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {detailData?.project?.invoice_status || selectedProject.invoice_status}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => { if (window.confirm('Create an invoice for this project?')) createInvoice.mutate(); }}
                    disabled={createInvoice.isLoading}
                    className="press w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50"
                  >
                    <Receipt className="h-4 w-4" />
                    {createInvoice.isLoading ? 'Creating invoice…' : 'Create Invoice'}
                  </button>
                )}
              </div>
            )}

            {/* Crew assignments */}
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Crew Assignments ({assignments.length}/{selectedProject.required_headcount || 1})
              </p>
              {assignments.length === 0
                ? <p className="text-sm text-gray-500 italic">No crew assigned yet.</p>
                : <div className="space-y-1 mb-3">
                    {assignments.map((a: any) => (
                      <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          {a.is_claimed && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                          <span className="text-sm text-gray-800">{a.crew_member_name}</span>
                          {a.is_claimed && <span className="text-xs text-green-600">claimed</span>}
                        </div>
                        <button onClick={() => {
                          if (window.confirm('Remove this crew member?'))
                            removeAssignment.mutate({ projectId: selectedProject.id, cmId: a.crew_member_id });
                        }} className="p-1 text-gray-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
              }
              {/* Add crew member */}
              <div className="flex gap-2">
                <select value={addingMember} onChange={e => setAddingMember(e.target.value)} className="input flex-1 text-sm">
                  <option value="">Add employee…</option>
                  {[...members].sort(byRecentAssignment).filter((m: any) => !assignedIds.has(m.id)).map((m: any) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                <button onClick={() => addAssignment.mutate()} disabled={!addingMember || addAssignment.isLoading}
                  className="btn-primary text-sm px-3 py-1.5">Add</button>
              </div>
            </div>

            {/* Photos */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Photos</p>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadPhotos.isLoading}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                  <Camera className="h-3 w-3" /> {uploadPhotos.isLoading ? 'Uploading…' : 'Add Photo'}
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={e => { if (e.target.files?.length) { uploadPhotos.mutate(e.target.files); e.target.value = ''; } }}
                />
              </div>
              {photos.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No photos yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((ph: any) => (
                    <div key={ph.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img src={`${UPLOADS_BASE}${ph.file_path}`} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => { if (window.confirm('Remove this photo?')) deletePhotoMut.mutate(ph.id); }}
                        className="absolute top-1 right-1 bg-black/60 text-snow rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="pt-4 border-t border-gray-100 mt-4 flex justify-between gap-2 flex-wrap">
            <button onClick={() => { openEdit(selectedProject); }}
              className="btn-secondary flex items-center gap-1.5">
              <Edit2 className="h-3.5 w-3.5" /> Edit
            </button>
            <div className="flex gap-2 flex-wrap">
              {!['complete', 'cancelled', 'invoiced', 'paid'].includes(selectedProject.status) && (
                <button onClick={() => setShowComplete(true)}
                  className="flex items-center gap-1.5 text-sm font-medium text-green-700 border border-green-300 rounded-lg px-3 py-2 hover:bg-green-50">
                  <CheckCircle className="h-3.5 w-3.5" /> Mark Complete
                </button>
              )}
              <button onClick={() => {
                if (window.confirm(`Delete "${selectedProject.title}"?`)) deleteProject.mutate(selectedProject.id);
              }} className="flex items-center gap-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
              <button onClick={() => { setModal(null); setSelectedProject(null); setShowComplete(false); setCompleteInvoice(null); }} className="btn-secondary">Close</button>
            </div>
          </div>
        </Modal>
      )}
      {/* Complete → invoice modal */}
      {showComplete && selectedProject && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Mark Complete</h3>
              <p className="text-sm text-gray-500 mt-0.5 truncate">{selectedProject.title}</p>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700 font-medium mb-2">Create an invoice now?</p>
              {lineItems.length > 0 ? (
                <p className="text-xs text-gray-500">
                  {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''} · Total:{' '}
                  <span className="font-semibold text-green-700">
                    {fmtMoney(lineItems.reduce((s: number, li: any) => s + (li.total || 0), 0))}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-amber-600">No line items added — invoice will use project price.</p>
              )}
            </div>
            <div className="px-5 pb-5 space-y-2">
              <button
                onClick={() => completeProject.mutate({ projectId: selectedProject.id, customerId: selectedProject.customer_id, createInv: true })}
                disabled={completeProject.isLoading}
                className="press w-full py-2.5 bg-brand-600 text-black text-sm font-semibold rounded-xl hover:bg-brand-500 disabled:opacity-50">
                {completeProject.isLoading ? 'Working…' : 'Create Invoice'}
              </button>
              <button
                onClick={() => completeProject.mutate({ projectId: selectedProject.id, customerId: selectedProject.customer_id, createInv: false })}
                disabled={completeProject.isLoading}
                className="w-full py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50">
                Complete Without Invoice
              </button>
              <button
                onClick={() => setShowComplete(false)}
                disabled={completeProject.isLoading}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-600">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── New project from a voice memo ────────────────────────────────────────────
// Dictate what the project is; that's the whole form (owner, Sep 28 2026:
// "most of the time it's just voice memo"). Pictures ride along optionally.
// When the memo's customer name matches more than one person, their cards
// come back for one tap — the memo is never re-typed.
function MemoModal({ customers, onClose, onDone }: { customers: any[]; onClose: () => void; onDone: () => void }) {
  const [memo, setMemo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<{ guess_name: string | null; candidates: any[]; draft: any } | null>(null);
  const [filter, setFilter] = useState('');
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  async function submit(customerId?: number) {
    if (memo.trim().length < 5) { toast.error('Say or type what the project is first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/projects/from-memo', {
        memo: memo.trim(),
        ...(customerId ? { customer_id: customerId } : {}),
        ...(ask?.draft && customerId ? { draft: ask.draft } : {}),
      });
      if (data.need_customer) {
        setAsk(data);
        setFilter(data.guess_name || '');
        return;
      }
      if (files.length && data.project_id) {
        const fd = new FormData();
        files.forEach(f => fd.append('photos', f));
        await api.post(`/projects/${data.project_id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => toast.warn('Project saved — the pictures did not upload, add them on the project'));
      }
      toast.success(data.message || 'Project drafted');
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not draft that');
    } finally { setBusy(false); }
  }

  const shown = ask
    ? (filter.trim()
        ? customers.filter((c: any) => c.name?.toLowerCase().includes(filter.trim().toLowerCase()))
        : (ask.candidates.length ? ask.candidates : customers)).slice(0, 8)
    : [];

  return (
    <Modal title="Project from a voice memo" onClose={onClose}>
      {!ask ? (
        <div className="space-y-3">
          <textarea value={memo} onChange={e => setMemo(e.target.value)} rows={6} autoFocus
            placeholder={'Tap the mic on your keyboard and just say it.\n"Bush removal for Nicole, three bushes out front, 450, this Thursday"'}
            className="input w-full text-[16px]" />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="press px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700">
              📷 Add pictures{files.length ? ` (${files.length})` : ''}
            </button>
            {files.length > 0 && <button type="button" onClick={() => setFiles([])} className="text-xs text-gray-500 underline">clear</button>}
            <div className="flex-1" />
            <button onClick={() => submit()} disabled={busy} className="btn-primary">{busy ? 'Drafting…' : 'Make the project'}</button>
          </div>
          <p className="text-[11px] text-gray-500">It lands as a draft with the memo kept in the notes — open it to tweak anything.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-800">Who is it for{ask.guess_name ? <> — I heard <b>{ask.guess_name}</b> but that matches {ask.candidates.length === 0 ? 'nobody' : 'more than one'}</> : null}?</p>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Type a name" className="input w-full" autoFocus />
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 overflow-hidden">
            {shown.map((c: any) => (
              <button key={c.id} onClick={() => submit(c.id)} disabled={busy}
                className="w-full text-left px-3 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50">{c.name}</button>
            ))}
            {!shown.length && <p className="px-3 py-2.5 text-sm text-gray-500">Nobody matches — check the spelling.</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900 truncate pr-4">{title}</h3>
          <button onClick={onClose} className="flex-shrink-0"><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
